import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Empty, Select, SelectProps, Spin, Typography } from 'antd'
import { FieldError } from 'react-hook-form'
import ErrorText from 'src/app/shared/components/common/error-text'
import './index.scss'
import { isArray } from 'lodash'

interface OptionProps {
  label: string
  value: any
}

const CustomSelectAll: React.FC<
  SelectProps & {
    label?: string
    required?: boolean
    error?: FieldError
    containerStyle?: React.CSSProperties
    haveAllOption?: boolean // select have "All" option in dropdown,
    defaultOption?: {
      label: React.ReactNode
      value: any
    }
    turnOffSort?: boolean
  }
> = (props) => {
  const {
    label,
    size = 'large',
    required,
    error,
    style,
    containerStyle,
    loading,
    options,
    value,
    haveAllOption,
    mode,
    onChange,
    defaultOption,
    onClick,
    onDropdownVisibleChange,
    turnOffSort = false,
    ...etc
  } = props
  const [open, setOpen] = useState<boolean | undefined>(haveAllOption ? false : undefined) // NOTE: "open" state is only used when having haveAllOption

  const selectMode = useMemo(() => {
    if (!haveAllOption) {
      // all cases not having "Select All" in dropdown
      return mode
    }
    // cases having "Select All" in dropdown
    if (value) {
      if (isArray(value) && value.includes(null)) {
        return undefined
      }
      return 'multiple'
    }
    return undefined
  }, [haveAllOption, mode, value])

  const handleSelect = useCallback(
    (value: string | Array<string | null>, option: OptionProps | Array<OptionProps>) => {
      if (haveAllOption) {
        if (value === null || (isArray(value) && value.includes(null))) {
          onChange?.(null, option)
        } else {
          !isArray(value) ? onChange?.([value], option) : onChange?.(value, option)
        }
      } else {
        onChange?.(value, option)
      }
    },
    [haveAllOption, onChange],
  )

  function sortByAlphabet(arr: any, field: string) {
    arr.sort((a: any, b: any) => {
      const lowerA = a?.[field]?.toLowerCase()
      const lowerB = b?.[field]?.toLowerCase()

      if (lowerA < lowerB) {
        return -1
      }
      if (lowerA > lowerB) {
        return 1
      }
      if (lowerA === lowerB) {
        if (a < b) {
          return -1
        }
        if (a > b) {
          return 1
        }
      }
      return 0
    })

    return arr
  }

  const sortOptions = () => {
    const optionsWithoutDefault = options?.filter((option) => option.value)
    const sortedArray = turnOffSort ? optionsWithoutDefault : sortByAlphabet(optionsWithoutDefault, 'label')
    if (defaultOption) {
      sortedArray.unshift(defaultOption)
    }
    return sortedArray
  }

  useEffect(() => {
    // NOTE: only change "open" state when having haveAllOption property
    // normal select doesnt need "open" state
    if (haveAllOption && selectMode === undefined) {
      setOpen(false)
    }
  }, [haveAllOption, selectMode])

  return (
    <div
      className='custom-select-all'
      style={{
        flex: 1,
        ...containerStyle,
      }}
    >
      <Typography.Text
        style={{ textAlign: 'left', fontSize: '14px', fontWeight: 700 }}
        className={`${required ? 'required' : ''} custom-select-label`}
      >
        {label}
      </Typography.Text>
      <Select
        style={{
          width: '100%',
          marginBottom: error ? 8 : 0,
          marginTop: 4,
          ...style,
        }}
        filterOption={(inputValue, option: any) => {
          return option?.label?.toLowerCase().includes(inputValue.toLowerCase())
        }}
        size={size}
        status={!!error ? 'error' : ''}
        notFoundContent={
          loading ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              <Spin />
            </div>
          ) : (
            <Empty />
          )
        }
        loading={loading}
        options={sortOptions()}
        mode={selectMode}
        value={value}
        onChange={handleSelect as any}
        open={open}
        onClick={(event) => {
          if (haveAllOption) {
            setOpen(true)
          }
          onClick?.(event)
        }}
        onDropdownVisibleChange={(visible) => {
          if (haveAllOption) {
            setOpen(visible)
          }
          onDropdownVisibleChange?.(visible)
        }}
        {...etc}
      />
      {error && <ErrorText>{error.message}</ErrorText>}
    </div>
  )
}

export default CustomSelectAll
